import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Search, Lock, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGitHubRepos } from "./useGitHubRepos";

export default function GitHubRepoSelect({ workflowId, value, onChange }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const sentinelRef = useRef(null);
    const { repos, search, setSearch, isLoading, error, hasMore, loadMore, initialize } =
        useGitHubRepos(workflowId);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
        if (!open || !sentinelRef.current) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting && hasMore) loadMore(); },
            { threshold: 0.1 }
        );
        obs.observe(sentinelRef.current);
        return () => obs.disconnect();
    }, [open, hasMore, loadMore]);

    const handleOpen = () => { setOpen((o) => !o); initialize(); };
    const handleSelect = (fullName) => { onChange?.({ gitHubRepo: fullName }); setOpen(false); };

    return (
        <div ref={containerRef} className="relative w-full">
            <button
                type="button"
                onClick={handleOpen}
                className="w-full flex items-center justify-between px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <span className={value ? "text-gray-900" : "text-gray-400"}>
                    {value || t("appPreview.gitHubSelectRepo")}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                    <div className="p-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 px-2 py-1 border border-gray-200 rounded">
                            <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <input
                                autoFocus
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t("appPreview.gitHubSearchRepos")}
                                className="flex-1 text-sm outline-none bg-transparent"
                            />
                        </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                        {isLoading && repos.length === 0 && (
                            <p className="px-3 py-4 text-sm text-center text-gray-400">{t("appPreview.gitHubLoadingRepos")}</p>
                        )}
                        {error && (
                            <p className="px-3 py-4 text-sm text-center text-red-500">{t("appPreview.gitHubReposError")}</p>
                        )}
                        {!isLoading && !error && repos.length === 0 && (
                            <p className="px-3 py-4 text-sm text-center text-gray-400">{t("appPreview.gitHubNoRepos")}</p>
                        )}
                        {repos.map((repo) => (
                            <button
                                key={repo.fullName}
                                type="button"
                                onClick={() => handleSelect(repo.fullName)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left ${value === repo.fullName ? "bg-blue-50 text-blue-700" : "text-gray-800"}`}
                            >
                                {repo.private
                                    ? <Lock className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                                    : <Globe className="w-3.5 h-3.5 shrink-0 text-gray-400" />}
                                {repo.fullName}
                            </button>
                        ))}
                        <div ref={sentinelRef} className="h-1" />
                        {isLoading && repos.length > 0 && (
                            <p className="px-3 py-2 text-xs text-center text-gray-400">{t("appPreview.gitHubLoadingRepos")}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
